import type { VideoSummary } from '../model/video.ts';
import { err, ok, parseError, type ParsedList, type ParseError, type Result } from '../result.ts';
import type { SearchSuggestions } from '../search/suggest.ts';
import type { ParseContext } from './context.ts';
import { asArray, asObject, asString, asStringArray, field } from './raw.ts';
import { parseVideoSummaryList } from './video.ts';

/**
 * `GET /api/v1/search/suggestions?q=…` → `{"query":"linu","suggestions":["linus tech tips",…]}`.
 *
 * The one shape worth naming: an **empty list is a success, not a failure.** `q=` answers
 * `{"query":"","suggestions":[]}` with a 200, and so does a query nothing matches. A parser that
 * treated no suggestions as a malformed response would make the ordinary case an error.
 *
 * A non-string entry drops itself rather than the page (`asStringArray` filters), because one odd
 * row in a list of hints is not worth withholding the other twelve.
 */
export function parseSearchSuggestions(raw: unknown): Result<SearchSuggestions, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('SearchSuggestions', null, 'wrong-type'));

  const suggestionsField = field(source, 'suggestions');
  // Absent is different from empty: `{}` is not this endpoint answering, it is something else
  // answering with JSON, and the caller should not show a dropdown built from a guess.
  if (asArray(suggestionsField) === null) {
    return err(parseError('SearchSuggestions', 'suggestions', 'wrong-type'));
  }

  return ok({
    // Echoed back normalised — `q=a++b` returns `"a b"` — which is exactly what makes it usable as a
    // staleness check (`suggestionsMatch`). Defaulted rather than required: the list is the payload.
    query: asString(field(source, 'query')) ?? '',
    suggestions: asStringArray(suggestionsField),
  });
}

/**
 * `GET /api/v1/hashtag/{tag}` — §8's `/hashtag/TAG` page, and the only endpoint in this build that
 * wraps its list in an **envelope**: `{"results":[…]}`, where search, popular and trending are all
 * bare arrays. So the mistake this parser exists to prevent is reading it as one of those and
 * reporting an empty page.
 *
 * Verified 2026-07-29: 36 rows of `type: "video"`, carrying the full search-shaped record. Three
 * findings that shape the page above it:
 *
 * - **An unknown tag is not a 404.** `/api/v1/hashtag/zzqqxxnope1234` answers `200 {"results":[]}`,
 *   and so does `/api/v1/hashtag/%23lofi` — the `#` must be stripped before the call, or every
 *   hashtag link renders as an empty page with nothing saying why.
 * - **`published` is back-computed**, like search's and unlike `/popular`'s: 36 rows share **2**
 *   distinct seconds-of-day. So no absolute-date tooltip.
 * - **Paging repeats rows.** `page=1` and `page=2` returned 36 each with 2 ids in common, so a page
 *   is a fresh window rather than a disjoint slice.
 */
export function parseHashtagFeed(raw: unknown, ctx: ParseContext): ParsedList<VideoSummary> {
  const source = asObject(raw);
  if (!source) return { items: [], dropped: [parseError('HashtagFeed', null, 'wrong-type')] };

  const results = asArray(field(source, 'results'));
  if (results === null) {
    return { items: [], dropped: [parseError('HashtagFeed', 'results', 'wrong-type')] };
  }

  return parseVideoSummaryList(results, ctx);
}
